import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORIES, CORPORATE_CATEGORIES, PERSONAL_CATEGORIES } from "@/lib/constants";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

export default function Catalogue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const audience = searchParams.get("audience");
  const [active, setActive] = useState(searchParams.get("category") || "all");
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (audience === "corporate") list = list.filter((p) => CORPORATE_CATEGORIES.includes(p.category));
    if (audience === "personal") list = list.filter((p) => PERSONAL_CATEGORIES.includes(p.category));
    if (active !== "all") list = list.filter((p) => p.category === active);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((p) =>
        [p.name, p.tagline, p.description].join(" ").toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, active, query, audience]);

  return (
    <main className="pt-32 pb-24 px-6 lg:px-12 max-w-7xl mx-auto min-h-screen">
      <Reveal>
        <p className="eyebrow">The Catalogue</p>
        <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl text-ink tracking-tight text-balance">
          {audience === "corporate" ? "Corporate gifting, curated." : audience === "personal" ? "For weddings & celebrations." : "Nine collections. One standard."}
        </h1>
        <p className="mt-4 max-w-xl text-base text-ink-muted leading-relaxed">
          Every piece can be tailored to your brand — foil, ribbon, engraving and message.
          No prices listed; each quote is composed around your brief.
        </p>
      </Reveal>

      <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="category-filter-all"
            onClick={() => setActive("all")}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.12em] border transition-colors duration-300 ${
              active === "all" ? "bg-ink text-cream border-ink" : "border-gold/20 text-ink/70 hover:border-gold/50"
            }`}
          >
            All
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              data-testid={`category-filter-${c.slug}`}
              onClick={() => setActive(c.slug)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.12em] border transition-colors duration-300 ${
                active === c.slug ? "bg-ink text-cream border-ink" : "border-gold/20 text-ink/70 hover:border-gold/50"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
        <div className="relative lg:w-72">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
          <input
            data-testid="catalogue-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search hampers..."
            className="w-full bg-white border border-gold/20 pl-11 pr-4 py-3 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-gold transition-colors duration-300"
          />
        </div>
      </div>

      {audience && (
        <button
          data-testid="audience-clear-button"
          onClick={() => setSearchParams({})}
          className="mt-6 text-xs font-mono uppercase tracking-[0.15em] text-gold-brass gold-underline-btn"
        >
          Showing {audience} picks — clear filter
        </button>
      )}

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filtered.map((p, i) => (
          <Reveal key={p.id} delay={(i % 3) * 0.08}>
            <ProductCard product={p} />
          </Reveal>
        ))}
      </div>

      {!loading && filtered.length === 0 && (
        <p data-testid="catalogue-empty-state" className="mt-20 text-center text-ink-muted">
          Nothing matches that search. Try another word, or{" "}
          <a href="/enquire" className="text-gold-brass underline underline-offset-4">ask us to curate it for you</a>.
        </p>
      )}
    </main>
  );
}
